// Copyright (c) Bentley Systems
import { AppDispatch } from "../app/store";
import { ConfigManager } from "../Helpers/config-manager";
import { setErrorState } from "../Helpers/helper-functions";
import { mapResponseToDTO } from "../Helpers/map-response";
import { VariableValidationRule, VariableValidationType } from "./Helpers/xml-variable-extractor";
import { ProjectArchitectDetail } from "./Models/project-architect-detail";

export class ProjectApiHelper {

  private static extractValidationRulesPayload(payload: unknown): unknown[] {
    if (Array.isArray(payload)) {
      return payload;
    }
    const record = payload as Record<string, unknown> | null | undefined;
    if (Array.isArray(record?.value)) {
      return record.value;
    }
    if (Array.isArray(record?.result)) {
      return record.result;
    }
    if (Array.isArray(record?.data)) {
      return record.data;
    }
    if (Array.isArray(record?.items)) {
      return record.items;
    }
    return [];
  }

  private static normalizeValidationRules(rawRules: unknown): VariableValidationRule[] {
    const items = ProjectApiHelper.extractValidationRulesPayload(rawRules);
    const normalizedRules: VariableValidationRule[] = [];

    for (const item of items) {
      const rule = item as Record<string, unknown>;
      const variableName = (rule.variableName ?? rule.VariableName) as string | undefined;
      const validationTypeRaw =
        (rule.validationTypeId ?? rule.ValidationTypeId ?? VariableValidationType.None) as string;
      const validationTypeId = Object.values(VariableValidationType).includes(validationTypeRaw as VariableValidationType)
        ? (validationTypeRaw as VariableValidationType)
        : VariableValidationType.None;

      if (!variableName) {
        continue;
      }

      normalizedRules.push({
        variableName,
        validationTypeId,
        isRequired: Boolean(rule.isRequired ?? rule.IsRequired),
        maxLength: (rule.maxLength ?? rule.MaxLength) as number | undefined,
        minValue: (rule.minValue ?? rule.MinValue) as number | undefined,
        maxValue: (rule.maxValue ?? rule.MaxValue) as number | undefined,
        allowDecimals: (rule.allowDecimals ?? rule.AllowDecimals) as boolean | undefined,
        regexPattern: (rule.regexPattern ?? rule.RegexPattern) as string | undefined,
        regexDescription: (rule.regexDescription ?? rule.RegexDescription) as string | undefined,
        dateFormat: (rule.dateFormat ?? rule.DateFormat) as string | undefined,
        allowedValues: (rule.allowedValues ?? rule.AllowedValues) as string | undefined,
      });
    }

    return normalizedRules;
  }

  // Reformats the stored one-line JSON so it's readable/editable in a textarea; falls back to the raw string if malformed.
  private static prettyPrintJson(value: string): string {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  public static async fetchProjectData({
    projId,
    accessToken,
    dispatch,
    runId,
    signal,
  }: {
    projId: string;
    accessToken: string;
    dispatch: AppDispatch;
    runId: string;
    signal?: AbortSignal;
  }): Promise<{
    projectDto?: ProjectArchitectDetail;
    error?: unknown;
  }> {
    const url =
      `${ConfigManager.projectProviderUrl}/odata/Projects(${encodeURIComponent(projId)})?$expand=Data($select=CompiledXml,ODataSchema),InputInfo`;
    try {
      const projectResponse = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: runId,
        },
        signal,
      });
      if (!projectResponse.ok) {
        setErrorState(projectResponse.status, dispatch, runId, true);
        return { error: "Project fetch failed" };
      }
      const projectData = await projectResponse.json();
      const projectDto: ProjectArchitectDetail = mapResponseToDTO<
        ProjectArchitectDetail,
        typeof projectData
      >(projectData);
      // Base64 decode
      if (projectDto.InputInfo) {
        projectDto.InputInfo.InputExample = projectDto.InputInfo.InputExample
          ? atob(projectDto.InputInfo.InputExample)
          : undefined;
        projectDto.InputInfo.ResponseExample = projectDto.InputInfo
          .ResponseExample
          ? atob(projectDto.InputInfo.ResponseExample)
          : undefined;
      }
      if (projectDto.Data) {
        projectDto.Data.CompiledXml = projectDto.Data.CompiledXml
          ? atob(projectDto.Data.CompiledXml)
          : undefined;
        projectDto.Data.ODataSchema = projectDto.Data.ODataSchema
          ? ProjectApiHelper.prettyPrintJson(atob(projectDto.Data.ODataSchema))
          : undefined;
      }
      projectDto.VariableValidations = ProjectApiHelper.normalizeValidationRules(
        projectDto.VariableValidations ?? [],
      );

      return { projectDto };
    } catch (error) {
      return { error };
    }
  }
}
